<template>
  <div class="algolia-filter">
    <div
      v-if="!loading"
      class="algolia-filter__inner"
      :class="{
        'algolia-filter__inner-max': pageType === 'collection'
      }"
    >
      <div class="algolia-filter__facets-wrap">
        <template
          v-for="(attribute, index) in formattedAttributes"
          :key="`${attribute.value}-${index}`"
        >
          <ais-refinement-list
            :attribute="attribute.value"
            :sort-by="sortingFunction(attribute.value)"
            :limit="70"
            class="algolia-filter__facet"
          >
            <template #default="{ items, refine }">
              <algolia-facet
                :label="attribute.label"
                :facets="items"
                @choices-updated="refine"
                @choices-reset="() => reset(refine, items)"
              />
            </template>
          </ais-refinement-list>
        </template>
      </div>

      <div class="algolia-filter__controls">
        <div
          v-click-outside="handlePageSizesClickOutside"
          class="algolia-filter__page-size"
          :class="{ [$class('active')]: pageSizesOpen }"
        >
          <button
            class="algolia-filter__page-size-trigger text-label-xs"
            aria-controls="page-size-options"
            :tabindex="0"
            type="button"
            @click="togglePageSizesOpen"
          >
            <span
              class="text-label-xs"
              v-text="$string('collection.page_size', {
                replace: {
                  size: pageSize,
                },
              })"
            />

            <icon-chevron-down />
          </button>

          <transition
            name="fade-down"
            mode="out-in"
          >
            <ul
              v-show="pageSizesOpen"
              id="page-size-options"
              class="algolia-filter__page-size-selection"
            >
              <li
                v-for="pageSizeOption in pageSizes"
                :key="pageSizeOption"
              >
                <button
                  class="algolia-filter__page-size-selection--option text-body-2xs"
                  :class="{ [$class('active')]: pageSizeOption == pageSize }"
                  :tabindex="open ? 0 : -1"
                  type="button"
                  @click="$emit('page-size-update', pageSizeOption)"
                  v-text="pageSizeOption"
                />
              </li>
            </ul>
          </transition>
        </div>

        <div class="algolia-filter__sort-wrapper">
          <ais-sort-by
            v-click-outside="handleSortClickOutside"
            class="algolia-filter__sort-by"
            :class="{ [$class('active')]: open }"
            :items="sortByOptions"
          >
            <template #default="{ items, currentRefinement, refine }">
              <button
                class="
                  algolia-filter__sort-by-label
                  text-label-xs
                "
                for="sort-by"
                :tabindex="0"
                type="button"
                @click="toggleOpen"
              >
                <span
                  class="
                    algolia-filter__sort-by-option
                    algolia-filter__sort-by-content
                    text-label-xs
                  "
                  v-text="getSortLabel(currentRefinement)"
                />

                <icon-chevron-down />
              </button>

              <transition
                name="fade-down"
                mode="out-in"
              >
                <ul
                  v-show="open"
                  class="algolia-filter__sort-by-selection"
                >
                  <li
                    v-for="sort in items"
                    :key="sort.id"
                  >
                    <button
                      :id="sort.id"
                      class="algolia-filter__sort-by-selection--option text-body-2xs"
                      :class="{ [$class('active')]: sort.value === currentRefinement }"
                      :tabindex="open ? 0 : -1"
                      type="button"
                      :value="sort.id"
                      js-algolia-filter="sortByOption"
                      @click="refine(sort.value)"
                    >
                      <span>
                        <component :is="getSortIcon(sort.value === currentRefinement)" />
                      </span>

                      <span
                        class="text-body-2xs"
                        v-text="sort.label"
                      />
                    </button>
                  </li>
                </ul>
              </transition>
            </template>
          </ais-sort-by>
        </div>
      </div>
    </div>

    <div
      v-else
      class="algolia-filter__inner"
      :class="{
        'algolia-filter__inner-max': pageType === 'collection'
      }"
    >
      <div class="algolia-filter__facets-wrap">
        <div
          v-for="(attribute, index) in formattedAttributes"
          :key="`${attribute.value}-${index}`"
          class="algolia-filter__facet"
          :class="[$class('loading')]"
          :style="{ animationDelay: animationDelay(index) }"
        />
      </div>

      <div class="algolia-filter__sort-wrapper">
        <div
          class="algolia-filter__page-size text-label-xs"
          :class="[$class('loading')]"
        />
        <div
          class="algolia-filter__sort-by text-label-xs"
          :class="[$class('loading')]"
        />
      </div>
    </div>

    <ais-current-refinements v-if="!loading && !isMobile">
      <template #default="{ items }">
        <div
          v-if="items.length"
          class="algolia-filters-slider"
        >
          <algolia-filters-slider
            :items="items"
          />
        </div>
      </template>
    </ais-current-refinements>
  </div>
</template>

<script>

/**
 * Vue: Algolia filter (main-collection)
 * -----------------------------------------------------------------------------
 * Collection and search filters.
 *
 * @param {boolean} loading - Show loading skeleton if true.
 * @param {number | string} plpColumnCount - PLP columns count.
 * @param {String} pageSizes - CSV options of page sizes.
 * @param {String} pageSize - page size.
 *
 */
import { mapState } from 'vuex'
import { AisRefinementList, AisHighlight, AisCurrentRefinements, AisSortBy } from 'vue-instantsearch/vue3/es'
import ClickOutside from 'vue-click-outside'

import AlgoliaFiltersSlider from '~async/main-collection/algolia-filters-slider'
import AlgoliaFacet from '~async/main-collection/algolia-facet'

import IconChevronDown from '~icons/direction/chevron/down.svg'
import IconCircleEmpty from '~icons/misc/circle-empty.svg'
import IconCircleFull from '~icons/misc/circle-full.svg'
import IconThreeColumn from '~icons/misc/three-column.svg'
import IconFourColumn from '~icons/misc/four-column.svg'

export default {
  name: 'AlgoliaFilter',

  components: {
    AlgoliaFiltersSlider,
    AlgoliaFacet,
    IconChevronDown,
    IconCircleEmpty,
    IconCircleFull,
    IconThreeColumn,
    IconFourColumn,
    AisRefinementList,
    AisHighlight,
    AisCurrentRefinements,
    AisSortBy,
  },

  directives: {
    ClickOutside,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    plpColumnCount: {
      type: [Number, String],
      default: 4,
    },
    pageSizes: {
      type: Array,
      default: () => [
        16, 32, 48, 64,
      ],
    },
    pageSize: {
      type: Number,
      default: 16,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'string',
    'page-size-update': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      attributes: this.$variable('algolia.filterOptions'),
      sortByOptions: this.$variable('algolia.sortByOptions'),
      pageType: this.$variable('page.type'),
      open: false,
      pageSizesOpen: false,
      /* eslint-disable array-element-newline */
      predefinedOrder: [
        'XS', 'S', 'M', 'L', 'XL', 'XXL', '1XL','2XL', '3XL', '4XL', '5XL',
        '1SZ', 'M/L', 'L/XL', '3', '4', '5', '6', '7', '7.5', '8', '8.5','9',
        '9.5', '10', '10.5', '11', '12', '13', '14', '28', '29', '30', '31',
        '32', '33', '34', '36', '38', '40', '28 / 30', '28 / 32', '30 / 30',
        '30 / 32', '30 / 34', '32 / 30', '32 / 32', '32 / 34', '34 / 30',
        '34 / 32', '34 / 34', '36 / 30', '36 / 32', '36 / 34', '38 / 32',
        '0-3 M', '3-6 M', '6-9 M', '9-12 M', '3/4 Y', '5/6 Y', '7/8 Y',
        '8/9 Y', '9/10 Y', '10/11 Y', '12/13 Y', '14/15 Y', '15/16 Y',
      ],
      /* eslint-enable array-element-newline */
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
     ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    formattedAttributes() {
      const preparedAttributes = this.attributes.map(attribute => {
        if (attribute.value === 'price') {
          const marketHandle = this.$variable('store.marketHandle')
          attribute.value = `price.${marketHandle}.amount`
        }

        return attribute
      })

      return preparedAttributes
    },
  },

  methods: {
    sortingFunction(facet) {
      if(facet !== 'sizesAvailable') {
        return ['count']
      }
      return (a, b) => {
        const aName = a?.name
        const bName = b?.name
        const indexA = this.predefinedOrder.indexOf(aName)
        const indexB = this.predefinedOrder.indexOf(bName)
        // If both elements exist in the predefinedOrder array
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        } else if (indexA !== -1) { // If only a exists in the predefinedOrder array
          return -1 // Move a to a lower index
        } else if (indexB !== -1) { // If only b exists in the predefinedOrder array
          return 1 // Move b to a lower index
        } else { // If neither a nor b exist in the predefinedOrder array
          return 0 // Maintain their relative order
        }
      }
    },

    /**
     * Compute animation delay.
     * @param {Number} index - Index of element in loop.
     * @returns {String}
     */
    animationDelay(index) {
      return this.$timing('quick', { index, unit: 's' })
    },

    /**
     * Toggle active class on facet click.
     * @param {Object} event - Click event.
     */
    toggleOpen() {
      this.open = !this.open
    },

    /**
     * Toggle active class on page size trigger click.
     * @param {Object} event - Click event.
     */
    togglePageSizesOpen() {
      this.pageSizesOpen = !this.pageSizesOpen
    },

    /**
     * Click outside event handle.
     */
    handlePageSizesClickOutside() {
      this.pageSizesOpen = false
    },

    /**
     * Click outside event handle.
     */
    handleSortClickOutside() {
      this.open = false
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {Boolean} isActive - is sort refinement active.
     */
    getSortIcon(isActive) {
      if (isActive) {
        return 'icon-circle-full'
      }

      return 'icon-circle-empty'
    },

    getSortLabel(index) {
      const activeSortOption = this.sortByOptions.find(sortOption => sortOption.value === index)

      if(activeSortOption) {
        return activeSortOption.label
      }
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {function(string): void} refine - algolia refine function.
     * @param {Array<object>} items - algolia facet items.
     */
    reset(refine, items) {
      items.forEach((item) => {
        if (item.isRefined) {
          refine(item.value) // Unselect all active refinements
        }
      })
    },
  },
}
</script>
